import type { GetServerSideProps } from 'next';

import { getLocaleFromParams } from '@/utils/locale';

import {
  fetchCMSDefaultConfig,
  fetchCmsPageDataWithExtraData,
  fetchNewsPages,
} from '@/integrations/contentful';
import { resolveContentDraftMode } from '@/lib/draftMode';
import { getGqlRegionFromLocale, getRegionFromLocale } from '@/lib/regions';
import { CmsView } from '@/views/CmsView';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const routerLocale = getLocaleFromParams(context.params);
  const { channel, languageCode } = getGqlRegionFromLocale(routerLocale);
  const { country, locale } = getRegionFromLocale(routerLocale);
  const draftMode = resolveContentDraftMode(context);

  const { cmsDefaultLayoutData } = await fetchCMSDefaultConfig(
    locale,
    country,
    draftMode,
  );

  const slugParam = context.params?.slug as string;
  const slugString = Array.isArray(slugParam)
    ? slugParam.join('/')
    : slugParam || '';

  const countryCode = country.slug;
  const contentfulLocale = locale.contentful ?? locale.locale;

  const {
    breadcrumb,
    cmsData,
    cmsExtraData,
    pageTitle,
    pageType,
    publishDate,
    seoConfigBuilder,
    shortDescription,
    tagsCollection,
  } = await fetchCmsPageDataWithExtraData(
    slugString,
    contentfulLocale,
    languageCode,
    channel,
    countryCode,
    draftMode,
  );

  if (!cmsData || cmsData.length === 0) {
    return { notFound: true };
  }

  const getPages = await fetchNewsPages(
    contentfulLocale,
    countryCode,
    draftMode,
  );

  const newsPages = getPages.pageCollection?.items;

  return {
    props: {
      cmsData,
      cmsExtraDataRecord: cmsExtraData,
      cmsDefaultLayoutData,
      breadcrumb,
      slug: slugString,
      country,
      locale,
      draftMode: draftMode,
      pageTitle,
      shortDescription,
      pageType,
      tagsCollection,
      publishDate,
      newsPages,
      seoConfig: seoConfigBuilder?.build(),
    },
  };
};

export default CmsView;
